/**
 * Platform switches (the `system` table, row 1) and the shared cache. Both are gated by the
 * OPERATORS bits on the server. Traps: `GET /api/system` always 400s (its default sort field doesn't
 * exist), so only row 1 is read; `POST` rejects `MaintenanceMode: 0`, so toggling is a PATCH.
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/api/http';
import { flag } from '@/api/zod-helpers';

export const SYSTEM_ID = 1;

/** The game servers (webapi) cache the flag for up to 300 s. */
export const MAINTENANCE_CACHE_SECONDS = 300;

export const systemSchema = z.object({ Id: z.coerce.number().int(), MaintenanceMode: flag });
export type SystemSettings = z.infer<typeof systemSchema>;

export const systemKeys = { settings: ['system', SYSTEM_ID] as const };

export const systemQuery = () =>
  queryOptions({
    queryKey: systemKeys.settings,
    queryFn: ({ signal }) => http(`/system/${SYSTEM_ID}`, { signal }, systemSchema),
  });

export function useSetMaintenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) =>
      http(`/system/${SYSTEM_ID}`, { method: 'PATCH', body: { MaintenanceMode: on ? 1 : 0 } }, systemSchema),
    onSuccess: (settings) => queryClient.setQueryData(systemKeys.settings, settings),
  });
}

const clearResultSchema = z.object({
  message: z.string().optional(),
  keysDeleted: z.coerce.number().int().optional(),
  keysSkipped: z.coerce.number().int().optional(),
});
export type ClearCacheResult = z.infer<typeof clearResultSchema>;

/** Flushes the shared Redis, keeping only zero-credit retry records. */
export function useClearCache() {
  return useMutation({
    mutationFn: () => http('/cache/clear-cache', { method: 'POST' }, clearResultSchema),
  });
}
